// @ts-strict-ignore
import { AfterViewInit, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import { ITableProvider } from '../table-provider';
import { VolatileDataSetHolder } from '@project/util/volatile-data-set.holder';
import { StringUtils } from '@shared/util/string-utils';
import { LabelEditorAssistantUtils } from './label-editor-assistant-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { LabelingUtils } from '@3d-scene/model3d/util/labeling-utils';

export interface LabelEditorAssistantStepper {
  labelNr: number;
}

export interface LabelEditorPENOptions {
  withPE: boolean;
  withN: boolean;
  selectedPESymbol: string;
  selectedNSymbol: string;
}

@Component({
  standalone: false,
  selector: 'label-editor-assistant',
  templateUrl: 'label-editor-assistant.component.html',
  styleUrls: ['./label-editor-assistant.component.scss'],
})
export class LabelEditorAssistantComponent implements OnInit, AfterViewInit {
  // provided data / options of label editor assistant
  public tableProvider: ITableProvider = null;
  public selectedCells: number[][] = null;
  public bsModalServiceSubscription: Subscription;
  @Output() public finish = new EventEmitter<{
    cellLabelList: [number, number, string][],
    selectedPENOptions: LabelEditorPENOptions,
  } | null>();
  public closeable: boolean = false;
  public isPENOptionsAvailable: boolean;
  // public withPE: boolean;
  // public withN: boolean;
  public symbolPEOptions: Object[];
  public symbolNOptions: Object[];
  // public selectedPESymbol: string;
  // public selectedNSymbol: string;
  public selectedPENOptions: LabelEditorPENOptions;

  public labelingOrderDataProvider: Object[];

  private _labelType: string = '';
  public isShowLeadingZeros: boolean = false;
  public showLeadingZerosEnabled: boolean = false;
  public numericFromText: string = '';
  public numericToText: string = '';
  public fromText: string = '';
  public toText: string = '';
  public fromToTextInputPattern: string = '';
  public fromToTextInputMaxChars: number = 0;
  public increment: number = 1;
  public prefixText: string = '';
  public suffixText: string = '';
  public headerText: string = '';
  public footerText: string = '';
  public repeatEntry: number = 1;
  public repeatCount: number = 1;

  private cellLabelList: {
    cellLabelList: [number, number, string][],
    selectedPENOptions: LabelEditorPENOptions,
  } | null = null;

  private onHideHandler = () => this.onHide();

  constructor(
    private bsModalRef: BsModalRef,
    private labelingUtils: LabelingUtils,
    private containerService: ContainerService,
    private bsModalService: BsModalService,
  ) {}

  public ngOnInit(): void {
    this.cellLabelList = null;
    let initLabelType = this.getDefaultValue('labelType');
    if (initLabelType !== 'alphabetic' && initLabelType !== 'numeric') {
      initLabelType = 'numeric';
    }
    this.labelType = initLabelType;
    this.labelingOrderDataProvider = this.getLabelingOrderDataProvider(
      this.tableProvider,
      this.selectedCells,
      this.getDefaultValue('labelingOrderId')
    );
    this.isShowLeadingZeros = this.getDefaultValue('showLeadingZeros') || false;
    if (initLabelType === 'numeric' && !StringUtils.isEmpty(this.numericFromText)) {
      this.fromText = this.numericFromText;
      this.toText = this.numericToText;
    } else if (this.selectedCellCount === this.getDefaultValue('selectedCellCount')) {
      this.fromText = this.getDefaultValue('fromText') || '';
      this.toText = this.getDefaultValue('toText') || '';
    }
    this.increment = this.getDefaultValue('increment') || 1;
    this.prefixText = this.getDefaultValue('prefixText') || '';
    this.suffixText = this.getDefaultValue('suffixText') || '';
    this.headerText = this.getDefaultValue('headerText') || '';
    this.footerText = this.getDefaultValue('footerText') || '';
    this.repeatEntry = this.getDefaultValue('repeatEntry') || 1;
    this.repeatCount = this.getDefaultValue('repeatCount') || 1;
  }

  public ngAfterViewInit(): void {
    this.bsModalServiceSubscription = this.bsModalService.onHide.subscribe(this.onHideHandler);
  }

  private onHide(): void {
    if (this.bsModalServiceSubscription) {
      this.bsModalServiceSubscription.unsubscribe();
    }
    this.finish.emit(this.cellLabelList);
  }

  private close(cellLabelList: {
    cellLabelList: [number, number, string][],
    selectedPENOptions: LabelEditorPENOptions,
  } | null = null): void {
    this.cellLabelList = cellLabelList;
    this.finish.emit(this.cellLabelList);
    this.bsModalRef.hide();
  }

  public cancel(): void {
    this.cellLabelList = null;
    this.finish.emit(this.cellLabelList);
    this.bsModalRef.hide();
  }

  private get selectedCellCount(): number {
    if (!this.selectedCells) {
      return 0;
    }
    return this.selectedCells.length;
  }

  public set labelType(labelType: string) {
    this._labelType = labelType;
    if (labelType === 'alphabetic') {
      this.fromToTextInputPattern = '^[a-zA-Z]*$';
      this.fromToTextInputMaxChars = 1;
      this.fromText = 'A';
      this.toText = String.fromCharCode(64 + Math.min(26, this.selectedCellCount));
      this.showLeadingZerosEnabled = false;
    } else {
      this.fromToTextInputPattern = '^[0-9]*$';
      this.fromToTextInputMaxChars = 10;
      if (StringUtils.isEmpty(this.numericFromText)) {
        this.fromText = '1';
        this.toText = this.selectedCellCount + '';
      } else {
        this.fromText = this.numericFromText;
        this.toText = this.numericToText;
      }
      this.showLeadingZerosEnabled = true;
    }
  }

  public get labelType(): string {
    return this._labelType;
  }

  private spansMultipleColumns(tableProvider: ITableProvider, selectedCells: number[][]): boolean {
    if (tableProvider === null) {
      return false;
    }
    if (selectedCells === null) {
      return tableProvider.colCount > 1;
    }
    let lastColIndex: number = -1;
    for (let i: number = 0; i < selectedCells.length; i++) {
      const selectedCell: number[] = selectedCells[i];
      if (lastColIndex === -1) {
        lastColIndex = selectedCell[1];
      } else if (lastColIndex !== selectedCell[1]) {
        return true;
      }
    }
    return false;
  }

  private spansMultipleRows(tableProvider: ITableProvider, selectedCells: number[][]): boolean {
    if (tableProvider === null) {
      return false;
    }
    if (selectedCells === null) {
      return tableProvider.rowCount > 1;
    }
    let lastRowIndex: number = -1;
    for (let i: number = 0; i < selectedCells.length; i++) {
      const selectedCell: number[] = selectedCells[i];
      if (lastRowIndex === -1) {
        lastRowIndex = selectedCell[0];
      } else if (lastRowIndex !== selectedCell[0]) {
        return true;
      }
    }
    return false;
  }

  private getLabelingOrderDataProvider(tableProvider: ITableProvider, selectedCells: number[][], lastSelectedLabelingOrderId: string): Object[] {
    const result: Object[] = [];
    if (tableProvider === null || (tableProvider.rowCount === 0 && tableProvider.colCount === 0)) {
      return result;
    }
    const spansMultipleRows: boolean = this.spansMultipleRows(tableProvider, selectedCells);
    const spansMultipleCols: boolean = this.spansMultipleColumns(tableProvider, selectedCells);
    if (spansMultipleCols && spansMultipleRows) {
      result.push({ id: 'rowsTl', icon: 'rows_tl.png', selected: lastSelectedLabelingOrderId === 'rowsTl' });
      result.push({ id: 'rowsTr', icon: 'rows_tr.png', selected: lastSelectedLabelingOrderId === 'rowsTr' });
      result.push({ id: 'rowsBl', icon: 'rows_bl.png', selected: lastSelectedLabelingOrderId === 'rowsBl' });
      result.push({ id: 'rowsBr', icon: 'rows_br.png', selected: lastSelectedLabelingOrderId === 'rowsBr' });
      result.push({ id: 'colsTl', icon: 'cols_tl.png', selected: lastSelectedLabelingOrderId === 'colsTl' });
      result.push({ id: 'colsTr', icon: 'cols_tr.png', selected: lastSelectedLabelingOrderId === 'colsTr' });
      result.push({ id: 'colsBl', icon: 'cols_bl.png', selected: lastSelectedLabelingOrderId === 'colsBl' });
      result.push({ id: 'colsBr', icon: 'cols_br.png', selected: lastSelectedLabelingOrderId === 'colsBr' });
    } else if (spansMultipleCols) {
      result.push({ id: 'colsL', icon: 'cols_l.png', selected: lastSelectedLabelingOrderId === 'colsL' });
      result.push({ id: 'colsR', icon: 'cols_r.png', selected: lastSelectedLabelingOrderId === 'colsR' });
    } else if (spansMultipleRows) {
      result.push({ id: 'rowsT', icon: 'rows_t.png', selected: lastSelectedLabelingOrderId === 'rowsT' });
      result.push({ id: 'rowsB', icon: 'rows_b.png', selected: lastSelectedLabelingOrderId === 'rowsB' });
    }
    let selectedOptionFound: boolean = false;
    optionLoop: for (const option of result) {
      if (option['selected']) {
        selectedOptionFound = true;
        break optionLoop;
      }
    }
    if (!selectedOptionFound && result.length > 0) {
      result[0]['selected'] = true;
    }
    return result;
  }

  public labelingOrderButtonClickHandler(event: MouseEvent, option: Object): void {
    for (const option of this.labelingOrderDataProvider) {
      option['selected'] = false;
    }
    option['selected'] = true;
  }

  private getSelectedLabelingOrderId(): string {
    if (!this.labelingOrderDataProvider || this.labelingOrderDataProvider.length === 0) {
      return null;
    }
    for (const option of this.labelingOrderDataProvider) {
      if (option['selected']) {
        return option['id'];
      }
    }
    return null;
  }

  private saveDefaultValues(): void {
    this.setDefaultValue('labelType', this.labelType);
    const selectedLabelingOrderId: string = this.getSelectedLabelingOrderId();
    if (selectedLabelingOrderId) {
      this.setDefaultValue('labelingOrderId', selectedLabelingOrderId);
    }
    if (this.showLeadingZerosEnabled) {
      this.setDefaultValue('showLeadingZeros', this.isShowLeadingZeros);
    }
    this.setDefaultValue('selectedCellCount', this.selectedCellCount);
    this.setDefaultValue('fromText', this.fromText);
    this.setDefaultValue('toText', this.toText);
    this.setDefaultValue('increment', this.increment);
    this.setDefaultValue('prefixText', this.prefixText);
    this.setDefaultValue('suffixText', this.suffixText);
    this.setDefaultValue('headerText', this.headerText);
    this.setDefaultValue('footerText', this.footerText);
    this.setDefaultValue('repeatEntry', this.repeatEntry);
    this.setDefaultValue('repeatCount', this.repeatCount);
  }

  private getDefaultValue(key: string): any {
    return VolatileDataSetHolder.instance.getDataSetValue(VolatileDataSetHolder.DATA_SET_LABEL_EDITOR_ASSISTANT, key);
  }

  private setDefaultValue(key: string, value: any): void {
    VolatileDataSetHolder.instance.setDataSetValue(VolatileDataSetHolder.DATA_SET_LABEL_EDITOR_ASSISTANT, key, value);
  }

  private getLabelValue(stepper: LabelEditorAssistantStepper, cell: number[]): string {
    if (this.isPENOptionsAvailable) {
      const specialLabelValue: string = LabelEditorAssistantUtils.getSpecialLabelValue(
        this.tableProvider,
        cell,
        this.selectedPENOptions,
        this.labelingUtils,
        this.containerService,
      );
      if (specialLabelValue) {
        stepper.labelNr--;
        return specialLabelValue;
      }
    }
    const fromText: string = this.fromText;
    if (StringUtils.isEmpty(fromText)) {
      return '';
    }
    let toText: string = this.toText;
    let valueSpanLength: number = 0;
    let fromInt: number = -1;
    let toInt: number = -1;
    if (this.labelType === 'alphabetic') {
      if (StringUtils.isEmpty(toText)) {
        toText = 'Z';
      }
      fromInt = fromText.charCodeAt(0);
      toInt = toText.charCodeAt(0);
      valueSpanLength = Math.abs(fromInt - toInt) + 1;
    } else {
      if (StringUtils.isEmpty(toText)) {
        toText = '999999999';
      }
      fromInt = parseInt(fromText, 10);
      toInt = parseInt(toText, 10);
      valueSpanLength = Math.abs(fromInt - toInt) + 1;
    }
    valueSpanLength = Math.ceil(valueSpanLength / this.increment);
    valueSpanLength *= this.repeatEntry;
    let labelNr = stepper.labelNr;
    const cycleNr: number = Math.floor(labelNr / valueSpanLength);
    if (cycleNr >= this.repeatCount) {
      return '';
    }
    labelNr %= valueSpanLength;
    labelNr = Math.floor(labelNr / this.repeatEntry);
    labelNr *= this.increment;
    if (fromInt <= toInt) {
      labelNr += fromInt;
    } else {
      labelNr = fromInt - labelNr;
    }
    let result: string = this.labelType === 'alphabetic' ? String.fromCharCode(labelNr) : labelNr + '';
    if (this.showLeadingZerosEnabled && this.isShowLeadingZeros) {
      const width: number = Math.max(Math.abs(fromInt).toString(10).length, Math.abs(toInt).toString(10).length);
      result = StringUtils.padWithZeros(parseInt(result, 10), width);
    }
    return result;
  }

  private getLabel(stepper: LabelEditorAssistantStepper, cell: number[]): string {
    const labelValue: string = this.getLabelValue(stepper, cell);
    if (labelValue === null) {
      return null;
    }
    let label: string = '';
    if (this.headerText !== '') {
      label += this.headerText + '\n';
    }
    label += this.prefixText;
    label += labelValue;
    label += this.suffixText;
    if (this.footerText !== '') {
      label += '\n' + this.footerText;
    }
    return label;
  }

  public executeLabeling(): void {
    if (this.tableProvider === null) {
      return;
    }
    // in case of alphabetic labeling, make sure both inputs are of the same lower-/upper-case
    if (this.labelType === 'alphabetic') {
      // fromInput is the leading value, toInput will be changed if necessary
      if (this.fromText && this.toText) {
        if (this.fromText.toLowerCase() !== this.fromText) {
          this.toText = this.toText.toUpperCase();
        } else if (this.fromText.toUpperCase() !== this.fromText) {
          this.toText = this.toText.toLowerCase();
        }
      }
    }
    const seqCells: number[][] = [];
    const selectedLabelingOrderId = this.getSelectedLabelingOrderId();
    if (!selectedLabelingOrderId || selectedLabelingOrderId === 'rowsTl' || selectedLabelingOrderId === 'rowsT') {
      for (let i: number = 0; i < this.tableProvider.rowCount; i++) {
        for (let j: number = 0; j < this.tableProvider.colCount; j++) {
          LabelEditorAssistantUtils.addSelectedCell(this.selectedCells, i, j, seqCells);
        }
      }
    } else if (selectedLabelingOrderId === 'rowsTr') {
      for (let i: number = 0; i < this.tableProvider.rowCount; i++) {
        for (let j: number = this.tableProvider.colCount - 1; j >= 0; j--) {
          LabelEditorAssistantUtils.addSelectedCell(this.selectedCells, i, j, seqCells);
        }
      }
    } else if (selectedLabelingOrderId === 'rowsBl' || selectedLabelingOrderId === 'rowsB') {
      for (let i: number = this.tableProvider.rowCount - 1; i >= 0; i--) {
        for (let j: number = 0; j < this.tableProvider.colCount; j++) {
          LabelEditorAssistantUtils.addSelectedCell(this.selectedCells, i, j, seqCells);
        }
      }
    } else if (selectedLabelingOrderId === 'rowsBr') {
      for (let i: number = this.tableProvider.rowCount - 1; i >= 0; i--) {
        for (let j: number = this.tableProvider.colCount - 1; j >= 0; j--) {
          LabelEditorAssistantUtils.addSelectedCell(this.selectedCells, i, j, seqCells);
        }
      }
    } else if (selectedLabelingOrderId === 'colsTl' || selectedLabelingOrderId === 'colsL') {
      for (let j: number = 0; j < this.tableProvider.colCount; j++) {
        for (let i: number = 0; i < this.tableProvider.rowCount; i++) {
          LabelEditorAssistantUtils.addSelectedCell(this.selectedCells, i, j, seqCells);
        }
      }
    } else if (selectedLabelingOrderId === 'colsTr' || selectedLabelingOrderId === 'colsR') {
      for (let j: number = this.tableProvider.colCount - 1; j >= 0; j--) {
        for (let i: number = 0; i < this.tableProvider.rowCount; i++) {
          LabelEditorAssistantUtils.addSelectedCell(this.selectedCells, i, j, seqCells);
        }
      }
    } else if (selectedLabelingOrderId === 'colsBl') {
      for (let j: number = 0; j < this.tableProvider.colCount; j++) {
        for (let i: number = this.tableProvider.rowCount - 1; i >= 0; i--) {
          LabelEditorAssistantUtils.addSelectedCell(this.selectedCells, i, j, seqCells);
        }
      }
    } else if (selectedLabelingOrderId === 'colsBr') {
      for (let j: number = this.tableProvider.colCount - 1; j >= 0; j--) {
        for (let i: number = this.tableProvider.rowCount - 1; i >= 0; i--) {
          LabelEditorAssistantUtils.addSelectedCell(this.selectedCells, i, j, seqCells);
        }
      }
    }
    const stepper: LabelEditorAssistantStepper = {
      labelNr: 0,
    };
    const cellLabelList: [number, number, string][] = [];
    for (const cell of seqCells) {
      const label: string = this.getLabel(stepper, cell);
      stepper.labelNr++;
      if (label === null || label === '') {
        continue;
      }
      cellLabelList.push([cell[0], cell[1], label]);
    }
    const result = {
      cellLabelList: cellLabelList,
      selectedPENOptions: this.selectedPENOptions,
    };
    this.saveDefaultValues();
    this.close(result);
  }

  public onPESymbolChange(): void {
    this.selectedPENOptions.withPE = true;
  }

  public onNSymbolChange(): void {
    this.selectedPENOptions.withN = true;
  }
}
